import { Box, Checkbox, Container, makeStyles, Typography, TextField } from '@material-ui/core';
import { useLoanStore } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isBefore } from 'date-fns';

import { Grid } from '@material-ui/core';
import { Theme } from 'react-select';
import { LoanDetailsCustomCard, LoanDetailsInfoItem } from '../components/loanDetailsCustomCard';
import { LoanDetailsPaper } from '../components/loanDetailsPaper';
import { DetailedAccordion } from 'libs/feature-one-tool/src/quote/steps/loanPricer/pricerSummaryTermV2';
import { useOneToolStore } from 'libs/feature-one-tool/src/quote/hooks/useOneToolStore';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { formatDate, getNameInCamelCase, isLenderOrBackOfficeUserRole, LoanStatus } from '@roc/feature-utils';
import { Button, InformationButton } from '@roc/ui';
import { ItemDisplay } from '@roc/feature-one-tool';
import { useUserStore } from '@roc/feature-app-core';
import { CustomQuote, CustomQuoteRequest } from '@roc/feature-types';
import { NotFullySubmittedAlert } from '../components/cards/notFullySubmittedAlert';
import { DownloadableQuotesDialog } from 'libs/feature-one-tool/src/quote/steps/loanPricer/downloadableQuoteDialog';
import { FULLY_AMORTIZING, FULLY_AMORTIZING_LABEL, PARTIAL_INTEREST_LABEL, PARTIAL_INTEREST } from 'libs/feature-one-tool/src/quote/stores/singlePropertyPricer/pricerSummaryHelper';

type Borrower = {
  personalGuarantor?: boolean;
  ficoProvidedAtOrigination?: number[];
};

const useStyles = makeStyles((theme: Theme) => ({
  pricingContainer: {
    '@media (max-width: 992px)': {
      marginLeft: '-6px',
    },
  },
  pricingCard: {
    marginBottom: '16px',
  },
  updateLogItem: {
    borderBottom: '1px solid #e0e0e0',
    marginLeft: '16px',
  },
  accordion: {
    border: 'none !important',
    width: '100%',
  },
  accordionSummary: {
    backgroundColor: 'transparent !important',
    borderBottom: 'none !important',
  },
  proposedTermEvaluated: {
    opacity: '0.5',
  },
  loanDetailsContent: {
    display: 'flex',
    textAlign: 'center',
  },
  customHeight: {
    height: '40vh',
  },
  underwriterComment: {
    paddingLeft: '10px',
  }
}));

const toNumber = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return typeof parsed === 'number' && !isNaN(parsed) ? parsed : null;
};

const mapAmortization = (value: string) => {
  if (value === FULLY_AMORTIZING) {
    return FULLY_AMORTIZING_LABEL;
  } else if (value === PARTIAL_INTEREST) {
    return PARTIAL_INTEREST_LABEL;
  }
  else {
    return value;
  }
};

type ChangeEntry = {
  previousValue: string | number | null;
  updatedValue: string | number | null;
};

type LogEntryProps = {
  value: string;
  label: string;
};

// Pure render function for loan term details
const getLoanTermDetails = (singlePropertyRentalQuoteStore, updateLoan, showLoanRatesDialog, setShowLoanRatesDialog, classes, swapRateToUse, hasPreloadedQuote, loanDetails) => {
  return (
    <Grid container>
      <Container className={classes.pricingCard}>
        <LoanDetailsPaper style={{ paddingBottom: 16, paddingTop: 16 }}>
          <Container>
            <DetailedAccordion store={singlePropertyRentalQuoteStore}
              setShowLoanRatesDialog={setShowLoanRatesDialog} onNext={() => { }} onSubmit={updateLoan} isPricingTab={true} hasPreloadedQuote={hasPreloadedQuote} loanDetails={loanDetails} />
          </Container>
        </LoanDetailsPaper>
      </Container>
      <DownloadableQuotesDialog
        store={singlePropertyRentalQuoteStore}
        open={showLoanRatesDialog}
        rateBuydownOptions={[]}
        onClose={() => setShowLoanRatesDialog(false)}
        swapRateToUse={swapRateToUse}
      />
    </Grid>
  );
};

const usePreloadedCustomQuote = (loanId: number | string | undefined) => {
  const { loanStore } = useLoanStore();
  const { oneToolStore } = useOneToolStore();
  const { singlePropertyRentalQuoteStore } = oneToolStore;
  const [isLoaded, setIsLoaded] = useState(false);
  const [quoteLoaded, setQuoteLoaded] = useState(false);
  const [dscrFetched, setDscrFetched] = useState(false);
  const [sliderSyncDone, setSliderSyncDone] = useState(false);
  const [customQuoteLoaded, setCustomQuoteLoaded] = useState(false);
  const [hasPreloadedQuote, setHasPreloadedQuote] = useState(false);
  const [loanToValue, setLoanToValue] = useState<{ value?: number } | undefined>();
  const [targetLTV, setTargetLTV] = useState<{ value?: number } | undefined>();

  // Step 1: Load quote and set dependent values
  useEffect(() => {
    if (!loanId) return;
    let isMounted = true;
    const loadQuote = async () => {
      setIsLoaded(false);
      setDscrFetched(false);
      setQuoteLoaded(false);
      setSliderSyncDone(false);
      setCustomQuoteLoaded(false);
      setHasPreloadedQuote(false);
      await singlePropertyRentalQuoteStore.loadQuoteToEdit(String(loanId));
      if (!isMounted) return;

      const pricerSummaryStore = singlePropertyRentalQuoteStore.pricerSummaryStore;
      const pricerStore = singlePropertyRentalQuoteStore.pricerStore;
      const quoteLoanToValue = pricerSummaryStore?.form.fields.loanToValue;
      const quoteTargetLTV = pricerStore?.form.fields.targetLTV;

      setLoanToValue(quoteLoanToValue);
      setTargetLTV(quoteTargetLTV);

      if (!pricerSummaryStore || !pricerStore) {
        setIsLoaded(true);
        setQuoteLoaded(false);
        return;
      }

      setQuoteLoaded(true);
    };

    loadQuote();

    return () => { isMounted = false; };
  }, [loanId, singlePropertyRentalQuoteStore]);

  // Step 2: Fetch DSCR rates after quote is loaded
  useEffect(() => {
    if (!quoteLoaded) return;
    let isMounted = true;

    const fetchDSCR = async () => {
      setDscrFetched(false);
      const pricerSummaryStore = singlePropertyRentalQuoteStore.pricerSummaryStore;
      if (!pricerSummaryStore) return;
      await pricerSummaryStore.fetchDSCRRates();
      if (isMounted) setDscrFetched(true);
    };

    fetchDSCR();

    return () => { isMounted = false; };
  }, [quoteLoaded, singlePropertyRentalQuoteStore]);

  const isPriceLockStillValid = () => {
    const priceLockDate = loanStore?.loanDetails?.priceLockDate;
    if (!priceLockDate) {
      return false;
    }
    const now = new Date();
    const firstLockExpirationDate = loanStore?.loanDetails?.firstLockExpirationDate;
    const secondLockExpirationDate = loanStore?.loanDetails?.secondLockExpirationDate;
    return (
      (!!firstLockExpirationDate && isBefore(now, new Date(firstLockExpirationDate))) ||
      (!!secondLockExpirationDate && isBefore(now, new Date(secondLockExpirationDate)))
    );
  }

  const getSwapRateToUse = () => {
    return isPriceLockStillValid() ? loanStore?.loanDetails?.loanPricingData?.swapRateAtAgreed : undefined;
  }

  // Step 3: Fetch rates and set loan to value after DSCR rates are ready
  useEffect(() => {
    if (!dscrFetched) return;
    let isMounted = true;

    const fetchRates = async () => {
      setIsLoaded(false);
      const pricerSummaryStore = singlePropertyRentalQuoteStore.pricerSummaryStore;
      const pricerStore = singlePropertyRentalQuoteStore.pricerStore;
      if (!pricerSummaryStore || !pricerStore) {
        if (isMounted) setIsLoaded(true);
        return;
      }

      const currentLTV = pricerSummaryStore.form.fields.loanToValue;
      const currentTarget = pricerStore.form.fields.targetLTV;

      await pricerSummaryStore.loadLenderInfo(loanStore.loanDetails.lenderId);
      pricerSummaryStore.pricingTabCompanyId = loanStore.loanDetails.companyId;
      if (!(currentLTV && currentLTV.value) && currentTarget && currentTarget.value !== undefined) {
        pricerSummaryStore.setLoanToValue(currentTarget.value);
      }
      await pricerSummaryStore.fetchRatesAndLoanTermsV2(getSwapRateToUse());
      await pricerSummaryStore.fetchRatesForCustomQuotes();

      if (loanStore.lastFetchedCustomQuote) {
        await pricerSummaryStore.loadCustomQuotes(loanStore.lastFetchedCustomQuote);
      }

      if (isMounted) setIsLoaded(true);
    };

    fetchRates();

    return () => { isMounted = false; };
  }, [dscrFetched, loanStore, singlePropertyRentalQuoteStore]);

  // Step 4: Re-run loan term change (slider effect) after everything is loaded
  useEffect(() => {
    if (!isLoaded || sliderSyncDone) return;
    const pricerSummaryStore = singlePropertyRentalQuoteStore.pricerSummaryStore;
    const currentLTV = pricerSummaryStore?.form.fields.loanToValue;
    if (!pricerSummaryStore || !currentLTV || currentLTV.value === undefined) return;

    const syncSliderEffect = async () => {
      pricerSummaryStore.isManualLtvSet = true;
      pricerSummaryStore.onFieldChange('loanToValue', currentLTV.value);
      await pricerSummaryStore.fetchRatesAndLoanTermsV2(getSwapRateToUse());
      await pricerSummaryStore.fetchRatesForCustomQuotes();
      setSliderSyncDone(true);
    };

    syncSliderEffect();
  }, [isLoaded, sliderSyncDone, singlePropertyRentalQuoteStore]);

  // Step 5: Load the last requested/proposed custom quote as a visible, editable row.
  useEffect(() => {
    if (!sliderSyncDone || customQuoteLoaded) return;
    let isMounted = true;

    const loadLastCustomQuote = async () => {
      const pricerSummaryStore = singlePropertyRentalQuoteStore.pricerSummaryStore;
      const lastQuote = loanStore.lastFetchedCustomQuote;

      const isPendingProposal = lastQuote?.action === 'Requested' || lastQuote?.action === 'Updated';

      if (pricerSummaryStore && lastQuote && isPendingProposal && pricerSummaryStore.customQuotes.length === 0) {
        const insertedIndex = await (pricerSummaryStore.loadQuoteFromChangeRequest(lastQuote, loanStore.loanDetails) as unknown as Promise<number>);

        if (insertedIndex >= 0) {
          if (isMounted) setHasPreloadedQuote(true);
        } else {
          console.warn('Unable to find a matching rate scenario to materialize the last custom quote.');
        }
      }

      if (isMounted) setCustomQuoteLoaded(true);
    };

    loadLastCustomQuote();

    return () => { isMounted = false; };
  }, [sliderSyncDone, customQuoteLoaded, loanStore, singlePropertyRentalQuoteStore]);

  // Re-triggers Step 5 so it re-materializes from the latest lastFetchedCustomQuote (e.g. after submitting/accepting).
  const resetPreloadedQuote = useCallback(() => {
    setHasPreloadedQuote(false);
    setCustomQuoteLoaded(false);
  }, []);

  return {
    isLoaded,
    customQuoteLoaded,
    hasPreloadedQuote,
    loanToValue,
    targetLTV,
    swapRateToUse: getSwapRateToUse(),
    resetPreloadedQuote,
  };
};

const LoanTermDetailsLoader = ({
  updateLoan,
  isLoaded,
  customQuoteLoaded,
  hasPreloadedQuote,
  loanToValue,
  targetLTV,
  swapRateToUse,
  loanDetails,
}) => {
  const classes = useStyles();
  const { oneToolStore } = useOneToolStore();
  const { singlePropertyRentalQuoteStore } = oneToolStore;
  const [showLoanRatesDialog, setShowLoanRatesDialog] = useState(false);

  if (!isLoaded || !customQuoteLoaded || !loanToValue || !targetLTV) return <Box p={4} textAlign="center">Loading Loan Terms...</Box>;

  return getLoanTermDetails(
    singlePropertyRentalQuoteStore,
    updateLoan,
    showLoanRatesDialog,
    setShowLoanRatesDialog,
    classes,
    swapRateToUse,
    hasPreloadedQuote,
    loanDetails
  );
};

interface LoanPricingTabProps {
  source: 'nportal' | 'cportal';
}

export const LoanPricingTab = observer(({ source }: LoanPricingTabProps) => {
  const { loanStore } = useLoanStore();
  const { loanDetails } = loanStore;
  const { userStore } = useUserStore();
  const { loanPurpose, properties, borrowers } = loanDetails;
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [underwriterComment, setUnderwriterComment] = useState('');
  const classes = useStyles();
  const [logs, setLogs] = useState<LogEntryProps[]>([]);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isSubmittingChangeRequest, setIsSubmittingChangeRequest] = useState(false);
  const { oneToolStore } = useOneToolStore();
  const { singlePropertyRentalQuoteStore } = oneToolStore;
  const preloadedCustomQuote = usePreloadedCustomQuote(loanDetails?.loanId);
  const lastCustomQuote = loanStore?.lastFetchedCustomQuote ?? null;
  const showReviseTermButton = lastCustomQuote?.action === 'Accepted' || lastCustomQuote?.action === 'Rejected' || lastCustomQuote?.action === 'Created';
  const lastProposedQuote = loanStore?.lastRequestedCustomQuote ?? null;
  const pending = loanDetails?.status === 'PENDING_SUBMISSION' || loanDetails?.status === 'PENDING';
  const hideReviseTerms = loanDetails?.status === LoanStatus.FUNDED || loanDetails?.status === LoanStatus.PAID_OFF;

  useEffect(() => {
    if (loanStore?.doAutoRefresh && !loanStore?.autoRefreshOn) {
      loanStore.setAutoRefreshOn(true);
      console.log('Will do auto refresh');
      setTimeout(function () {
        window.location.reload();
      }, 180000);
    }
  }, [loanStore?.doAutoRefresh]);

  const getRevisedProposedMessage = () => {
    if (pending) {
      return lastCustomQuote?.source === 'cportal' ? 'Proposed' : 'Revised';
    }
    else {
      return lastCustomQuote?.source === 'cportal' ? 'Revised' : 'Proposed';
    }
  };

  const pricingReviseContainer = ({ quote }) => {
    return (
      <Grid container className={classes.pricingCard} item xs={12} spacing={2}>
        <Grid item xs={3}>
          <Typography variant="h6">
            {(quote?.action === 'Created' || quote?.action === 'Requested' || quote?.action === 'Accepted') ? 'Proposed Terms' : 'Revised Terms'}
          </Typography>
        </Grid>
        <Grid
          item
          container
          xs={9}
          spacing={2}
          justifyContent="space-between"
          alignItems="stretch"
          className={classes.loanDetailsContent}
        >
          <Grid item>
            <ItemDisplay
              label="Loan Rate"
              value={quote?.rate ? `${quote?.rate.toFixed(2)}%` : '-'}
            />
          </Grid>
          <Grid item>
            <ItemDisplay
              label="Amortization Type"
              value={quote?.displayAmortizationLabel ? `${quote?.displayAmortizationLabel}` : '-'}
            />
          </Grid>
          <Grid item>
            <ItemDisplay
              label="Rate Type"
              value={quote?.rateType ? `${quote?.rateType}` : '-'}
            />
          </Grid>
          <Grid item>
            <ItemDisplay
              label="Pre-Payment Penalty"
              value={quote?.prePaymentPenalty ? `${quote?.prePaymentPenalty}` : '-'}
            />
          </Grid>
          <Grid item>
            <ItemDisplay
              label="Loan to Value"
              value={(quote?.loanToValue ? `${quote?.loanToValue}%` : '-')}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderUnderwriterComment = () => {
    if (source !== 'nportal') return null;
    return (
      <Container className={classes.pricingCard}>
        <LoanDetailsPaper style={{ paddingBottom: 16, paddingTop: 16 }}>
          <Container>
            <Typography variant="h5" gutterBottom>
              Underwriter Comment
            </Typography>
            <Typography variant="h6" style={{ opacity: 0.5 }}>
              Please provide any additional details or context here.
            </Typography>
            <TextField
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              placeholder="Add comments for this request"
              value={underwriterComment}
              onChange={e => setUnderwriterComment(e.target.value)}
            />
          </Container>
        </LoanDetailsPaper>
      </Container>
    );
  };

  const loadLoanChangeRequestLogs = useCallback(() => {
    const changeRequests = Array.isArray(loanStore.customQuotes) ? loanStore.customQuotes : [];
    if (!changeRequests.length) {
      setLogs([]);
      return;
    }

    const toPercent = (value?: number | null, fractionDigits = 2) => {
      if (value === null || value === undefined) return undefined;
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return undefined;
      const normalized = numeric > 1 ? numeric : numeric * 100;
      return `${normalized.toFixed(fractionDigits)}%`;
    };

    const entries = changeRequests
      .map((request: CustomQuoteRequest): LogEntryProps | null => {
        if (!request) return null;

        const actorRole = request?.actorRole?.trim();
        const actorName = request?.actorName?.trim();
        const [firstName, lastName] = actorName.split(' ');
        const actor = isLenderOrBackOfficeUserRole(actorRole) ? actorName : getNameInCamelCase(firstName, lastName) ?? 'Unknown';
        const action = (request?.action ?? '').toLowerCase();


        let actionPhrase: string;
        switch (action) {
          case 'created':
            actionPhrase = 'accepted a';
            break;
          case 'requested':
            actionPhrase = 'requested a';
            break;
          default:
            actionPhrase = `${action} the request for a`;
            break;
        }

        const rateText = toPercent(request?.rate) ?? '-';
        const amortizationText = request?.amortization ? mapAmortization(request?.amortization) : '-';
        const rateTypeText = request?.rateType || '-';
        const prepayText = request?.prePaymentPenalty || '-';
        const ltvText = toPercent(request?.loanToValue, 0) ?? '-';
        const createdDate = request?.createdDate ?? null;

        const label = createdDate
          ? formatDate(createdDate, "MMM d, yyyy 'at' h:mm a")
          : '-';

        const value = `${actor} ${actionPhrase} Loan Rate of ${rateText}, ${amortizationText}, Rate Type of ${rateTypeText}, Pre-Payment Penalty of ${prepayText}, LTV of ${ltvText}`;

        return {
          value,
          label,
        };
      })
      .filter((entry): entry is LogEntryProps => !!entry);

    setLogs(entries);
    console.log("Setting logs to:", entries);
  }, [loanStore.customQuotes]);

  const refreshLoanChangeRequests = useCallback(async () => {
    if (!loanDetails?.loanId) return;
    await loanStore.fetchLoanChangeRequests(loanDetails?.loanId);
    loadLoanChangeRequestLogs();
  }, [loanDetails?.loanId, loanStore]);

  const fetchLoanChangeRequests = useCallback(async () => {
    if (!loanDetails?.loanId) return;
    refreshLoanChangeRequests();
  }, [loanDetails?.loanId, refreshLoanChangeRequests]);

  useEffect(() => {
    fetchLoanChangeRequests();
  }, [loanDetails?.loanId, fetchLoanChangeRequests]);

  const getLoanToValue = () => {
    const loanAmount = loanDetails?.amount;
    const asIsValue = getAsIsValue();
    if (typeof loanAmount !== 'number' || typeof asIsValue !== 'number' || asIsValue === 0) return undefined;
    return (loanAmount / asIsValue);
  };

  const getAsIsValue = () => {
    if (!properties || !Array.isArray(properties) || properties.length === 0) return undefined;
    const prop = properties[0];
    if (prop.appraisalValue1 && prop.appraisalValue1 !== 0) return prop.appraisalValue1;
    if (prop.underwrittenPropertyValuation && prop.underwrittenPropertyValuation !== 0) return prop.underwrittenPropertyValuation;
    if (prop.propertyValuationAtOrigination && prop.propertyValuationAtOrigination !== 0) return prop.propertyValuationAtOrigination;
    return undefined;
  };
  const getPersonalGuarantorFico = () => {
    const borrowersArr: Borrower[] = borrowers ? Array.from(borrowers) as Borrower[] : [];
    const personalGuarantors = borrowersArr.filter((b: any) => !!b.personalGuarantor);
    if (personalGuarantors.length === 0) return undefined;

    // Helper to extract max from number or array
    const extractMax = (val: any) => {
      if (Array.isArray(val)) {
        return val.length > 0 ? Math.max(...val) : undefined;
      }
      return typeof val === 'number' && !isNaN(val) ? val : undefined;
    };

    // Seek medianScore first
    const medianScores = personalGuarantors
      .map((b: any) => extractMax(b.medianScore))
      .filter((score: any) => typeof score === 'number' && !isNaN(score));
    if (medianScores.length > 0) {
      return Math.max(...medianScores);
    }

    // Seek ficoProvidedAtOrigination if no valid medianScore
    const ficoScores = personalGuarantors
      .map((b: any) => extractMax(b.ficoProvidedAtOrigination))
      .filter((score: any) => typeof score === 'number' && !isNaN(score));
    if (ficoScores.length > 0) {
      return Math.max(...ficoScores);
    }
    return undefined;
  };

  const submitCustomQuoteChangeRequest = async (
    customQuote: CustomQuote,
    action: 'Requested' | 'Updated' | 'Accepted'
  ) => {
    if (!loanDetails?.loanId || !customQuote) {
      console.log('Missing loan or custom quote, aborting submission.');
      return;
    }
    if (isSubmittingChangeRequest) {
      console.log('Change request submission already in progress, ignoring duplicate call.');
      return;
    }

    setIsSubmittingChangeRequest(true);
    try {
      await submitCustomQuoteChangeRequestInternal(customQuote, action);
    } finally {
      setIsSubmittingChangeRequest(false);
    }
  };

  const submitCustomQuoteChangeRequestInternal = async (
    customQuote: CustomQuote,
    action: 'Requested' | 'Updated' | 'Accepted'
  ) => {
    const pricerSummaryStore = singlePropertyRentalQuoteStore.pricerSummaryStore;

    // Only the quote actually being submitted/accepted survives - discard any other
    for (let i = pricerSummaryStore.customQuotes.length - 1; i >= 0; i--) {
      if (pricerSummaryStore.customQuotes[i] !== customQuote) {
        pricerSummaryStore.removeCustomQuote(i);
      }
    }

    // Sync the store to this quote so getQuoteData() reflects its rate/LTV/fees.
    pricerSummaryStore.selectCustomQuote(pricerSummaryStore.customQuotes.indexOf(customQuote));
    const baseQuoteData = singlePropertyRentalQuoteStore.getQuoteData();

    // Fees always fresh from the loan, never editable.
    if (baseQuoteData?.loanLenderDetails) {
      baseQuoteData.loanLenderDetails = pricerSummaryStore.applyLoanLenderFeesFromLoan(baseQuoteData.loanLenderDetails, loanDetails);
    }

    const pricerLoanTerms = customQuote.pricerLoanTerms ?? {};

    const loanChangeRequest: any = {
      loanId: loanDetails.loanId,
      action,
      loanStatus: loanDetails?.status ?? 'Unknown',
      actorRole: userStore?.userInformation?.roles?.[0] ?? '',
      actorName: userStore?.userInformation?.fullName ?? '',
      source: source ?? 'cportal',
      loanAmount: toNumber(pricerLoanTerms?.amount ?? loanDetails?.amount),
      loanPurpose: customQuote.loanPurpose ?? loanDetails?.loanPurpose ?? null,
      loanQuote: baseQuoteData,
      comments: underwriterComment,
    };

    if (customQuote.amortization) {
      loanChangeRequest.amortizationType = customQuote.amortization;
    }
    if (customQuote.rateType) {
      loanChangeRequest.rateType = customQuote.rateType;
    }
    if (customQuote.prePaymentPenalty) {
      loanChangeRequest.prepayPenalty = customQuote.prePaymentPenalty;
    }
    const ltvPct = toNumber(customQuote.loanToValue);
    if (ltvPct !== null) {
      loanChangeRequest.ltvPct = ltvPct;
    }
    const ratePct = toNumber(customQuote.rate ?? pricerLoanTerms?.rateChargedToBorrower ?? pricerLoanTerms?.rate);
    if (ratePct !== null) {
      loanChangeRequest.ratePct = ratePct;
    }
    const minRatePct = toNumber(customQuote.minRate);
    if (minRatePct !== null) {
      loanChangeRequest.minRatePct = minRatePct;
    }
    const maxRatePct = toNumber(customQuote.maxRate);
    if (maxRatePct !== null) {
      loanChangeRequest.maxRatePct = maxRatePct;
    }
    const yieldSpreadPct = toNumber(customQuote.yieldSpread);
    if (yieldSpreadPct !== null) {
      loanChangeRequest.yieldSpreadPct = yieldSpreadPct;
    }

    // Fields that don't change per submission
    const carryForward: any = lastCustomQuote;
    if (carryForward?.loanToCost != null) {
      loanChangeRequest.loanToCost = carryForward.loanToCost;
    }
    if (carryForward?.totalLoanToCost != null) {
      loanChangeRequest.totalLoanToCost = carryForward.totalLoanToCost;
    }
    if (carryForward?.arltv != null) {
      loanChangeRequest.arltv = carryForward.arltv;
    }
    if (carryForward?.constructionReserve != null) {
      loanChangeRequest.constructionReserve = carryForward.constructionReserve;
    }
    if (carryForward?.interestRate != null) {
      loanChangeRequest.interestRate = carryForward.interestRate;
    }
    if (carryForward?.loanTermMonths != null) {
      loanChangeRequest.loanTermMonths = carryForward.loanTermMonths;
    }

    await loanStore.submitLoanChangeRequest(loanChangeRequest);

    // Drop the local customQuote(s) so the tab re-syncs from the just-submitted request instead of showing stale data.
    for (let i = pricerSummaryStore.customQuotes.length - 1; i >= 0; i--) {
      pricerSummaryStore.removeCustomQuote(i);
    }
    setIsSubmitDialogOpen(false);
    await refreshLoanChangeRequests();
    preloadedCustomQuote.resetPreloadedQuote();
  };

  const updateLoan = async (customQuote?: CustomQuote) => {
    if (!customQuote) {
      console.log('No custom quote selected for submission.');
      return;
    }
    await submitCustomQuoteChangeRequest(customQuote, source === 'cportal' ? 'Requested' : 'Updated');
  };

  const approveProposedTerms = async () => {
    if (!preloadedCustomQuote.hasPreloadedQuote) {
      console.warn('No preloaded quote available yet, aborting approval.');
      return;
    }
    const acceptedQuote = singlePropertyRentalQuoteStore.pricerSummaryStore?.customQuotes?.[0];
    if (!acceptedQuote) {
      console.warn('Preloaded quote row not found, aborting approval.');
      return;
    }

    await submitCustomQuoteChangeRequest(acceptedQuote, 'Accepted');
  };

  const loanTermAlert = useMemo(() => {
    if (!lastCustomQuote) {
      return { message: '', backgroundColor: '' };
    }

    if (lastCustomQuote.action === 'Created') {
      return { message: 'Proposed Terms Accepted', backgroundColor: '#EDF7ED' };
    }

    if (lastCustomQuote.action === 'Requested' || lastCustomQuote.action === 'Updated') {
      return { message: 'Submitted for Review', backgroundColor: '#FFF4E5' };
    }

    if (lastCustomQuote.action === 'Accepted') {
      const message = getRevisedProposedMessage() + ' Terms Accepted';
      return { message, backgroundColor: '#EDF7ED' };
    }

    return { message: '', backgroundColor: '' };
  }, [lastCustomQuote]);

  const returnLoanTermAlert = () => (
    (loanTermAlert.message !== '') && (
      <Typography
        variant="body2"
        style={{
          backgroundColor: loanTermAlert.backgroundColor,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: loanTermAlert.backgroundColor,
          padding: '4px 8px',
          borderRadius: 10
        }}
      >
        {`${loanTermAlert.message}`}
      </Typography>)
  );

  return (
    <>
      {loanDetails?.fullySubmitted ?
        <Container className={classes.pricingContainer}>
          {source === 'cportal' && (
            <Container className={classes.pricingCard}>
              <LoanDetailsPaper style={{ paddingBottom: 16, paddingTop: 16 }}>
                <Container>
                  <Box fontSize={26} fontWeight={600}>
                    Disclaimer
                  </Box>
                </Container>
                <Container>
                  <Box paddingBottom={2} fontSize={14}>
                    You can update your pricing here at any time - If the loan is in pending it will automatically update, but if it is in any other stage you will have to wait for the underwriter to confirm prior to portal getting updated. The below inputs are based on the most up to date information that we have. If you believe any of the information to be wrong, please contact the underwriter in that task to get it resolved. Please note this repricing affects buy down fees only - if you want to change your add on fees please go to the Lender Fees task.
                  </Box>
                </Container>
              </LoanDetailsPaper>
            </Container>
          )}
          <Container className={classes.pricingCard}>
            <LoanDetailsCustomCard>
              <Grid container spacing={2} justifyContent='center' className={classes.loanDetailsContent}>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.address}`}
                        label="PROPERTY ADDRESS"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${loanPurpose}`}
                        label="LOAN PURPOSE"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.propertyType}`}
                        label="PROPERTY TYPE"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.acquisitionPrice ? `${formatCurrency(properties[0]?.acquisitionPrice)}` : '-'}`}
                        label="PURCHASE PRICE"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.acquisitionDate ? formatDate(properties[0]?.acquisitionDate, 'MMMM d, yyyy') : '-'}`}
                        label="PURCHASE DATE"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.acquisitionPrice ? `${formatCurrency(properties[0]?.acquisitionPrice)}` : '-'}`}
                        label="COMPLETED REHAB"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${formatCurrency(getAsIsValue())}`}
                        label="AS-IS VALUE"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.monthlyGrossRent ? `${formatCurrency(properties[0]?.monthlyGrossRent)}` : '-'}`}
                        label="MARKET RENT"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.monthlyGrossRent ? `$${properties[0]?.monthlyGrossRent.toLocaleString()}` : '-'}`}
                        label="LEASED RENT"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.annualTaxes ? `${formatCurrency(properties[0]?.annualTaxes)}` : '-'}`}
                        label="ANNUAL TAXES"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.annualInsurance ? `${formatCurrency(properties[0]?.annualInsurance)}` : '-'}`}
                        label="ANNUAL INSURANCE"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${properties[0]?.annualHOA ? `${formatCurrency(properties[0]?.annualHOA)}` : '-'}`}
                        label="ANNUAL HOA"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={3}>
                      <LoanDetailsInfoItem
                        value={`${getPersonalGuarantorFico() ?? '-'}`}
                        label="PERSONAL GUARANTORS'S FICO"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </LoanDetailsCustomCard>
          </Container>
          <Container className={classes.pricingCard}>
            <LoanDetailsCustomCard >
              <Grid container className={classes.pricingCard}>
                <Grid item xs={6}>
                  <Box fontSize={20} fontWeight={600}>
                    Pricing Terms
                  </Box>
                </Grid>
                <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box>
                    {returnLoanTermAlert()}
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.pricingCard}>
                <LoanDetailsCustomCard>
                  <Grid container>
                    <Grid container>
                      {/* logic when proposed  and then logic when accepted o created */}
                      {(lastCustomQuote?.action === 'Accepted' || lastCustomQuote?.action === 'Created' || lastCustomQuote?.action === 'Rejected') && (
                        <Grid container className={`${classes.pricingCard} ${classes.proposedTermEvaluated}`} item xs={12} spacing={2}>
                          <Grid item xs={3}>
                            <Typography variant="h6">
                              {getRevisedProposedMessage() + ' Terms'}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            container
                            xs={9}
                            spacing={2}
                            justifyContent="space-between"
                            alignItems="stretch"
                            className={classes.loanDetailsContent}
                          >
                            <Grid item>
                              <ItemDisplay
                                label="Loan Rate"
                                value={loanDetails?.rate ? `${loanDetails?.rate.toFixed(2)}%` : '-'}
                              />
                            </Grid>
                            <Grid item>
                              <ItemDisplay
                                label="Amortization Type"
                                value={loanDetails?.amortization ? `${loanDetails?.amortization}` : '-'}
                              />
                            </Grid>
                            <Grid item>
                              <ItemDisplay
                                label="Rate Type"
                                value={loanDetails?.rateType ? `${loanDetails?.rateType}` : '-'}
                              />
                            </Grid>
                            <Grid item>
                              <ItemDisplay
                                label="Pre-Payment Penalty"
                                value={loanDetails?.prepaymentDescription ? `${loanDetails?.prepaymentDescription}` : '-'}
                              />
                            </Grid>
                            <Grid item>
                              <ItemDisplay
                                label="Loan to Value"
                                value={(() => {
                                  const ltv = getLoanToValue();
                                  return ltv ? formatPercentage(ltv * 100) : '-';
                                })()}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      {(lastCustomQuote?.action === 'Requested') && (
                        pricingReviseContainer({ quote: lastCustomQuote })
                      )}
                      {(lastCustomQuote?.action === 'Updated') && (
                        <>
                          <Grid container>
                            {pricingReviseContainer({ quote: lastProposedQuote })}
                          </Grid>
                          <Grid container>
                            {pricingReviseContainer({ quote: lastCustomQuote })}
                          </Grid>
                        </>
                      )}
                    </Grid>
                    { ((lastCustomQuote?.action === 'Updated' || lastCustomQuote?.action === 'Requested') && lastCustomQuote?.comments) &&
                      (<Grid container xs={12} spacing={2} direction="column" className={classes.pricingCard}>
                        <Container className={classes.underwriterComment}>
                          <Box fontSize={16} fontWeight={600}>
                            Underwriter Comments
                          </Box>
                        </Container>
                        <Container className={classes.underwriterComment}>
                          <Box paddingBottom={2} fontSize={14}>
                            {lastCustomQuote?.comments}
                          </Box>
                        </Container>
                      </Grid>)
                    }
                    {((lastCustomQuote?.action === 'Updated') && source === 'cportal') && (
                      <Grid container xs={12} spacing={2} direction="column" className={classes.pricingCard}>
                        <Container>
                          <Grid container alignItems="center" wrap='nowrap'>
                            <Grid item>
                              <Checkbox
                                checked={hasAcceptedTerms}
                                onChange={(_, checked) => setHasAcceptedTerms(checked)}
                              />
                            </Grid>
                            <Grid item xs={11}>
                              <Typography variant="caption" style={{ display: 'inline', marginLeft: 8 }}>
                                By accepting, as a third party acting on behalf of the borrower, I certify that the borrower has been informed of and agrees to the loan terms outlined on this page.
                              </Typography>
                            </Grid>
                          </Grid>

                        </Container>
                        <Container>
                          <Grid container alignItems="center" style={{ marginTop: 16 }}>
                            <Grid item xs={6}>
                              <Box mr={2} display="flex" justifyContent="flex-end">
                                <InformationButton
                                  hidden={true}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  testId="editLoanTerms"
                                  onClick={() => console.log('Negotiate Terms')}
                                  informationDialogProps={{
                                    title: 'Revise Pricing Terms',
                                    body: (
                                      <>
                                        {renderUnderwriterComment()}
                                        <LoanTermDetailsLoader
                                          updateLoan={updateLoan}
                                          {...preloadedCustomQuote}
                                          loanDetails={loanDetails}
                                        />
                                      </>
                                    ),
                                    maxWidth: 'lg',
                                    dialogOpen: isSubmitDialogOpen,
                                    setDialogOpen: setIsSubmitDialogOpen,
                                  }}
                                  disabled={hideReviseTerms}
                                >
                                  REVISE TERMS
                                </InformationButton>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box mr={2} display="flex" justifyContent="flex-start">
                                <Button
                                  hidden={true}
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  testId="editLoanData"
                                  disabled={!hasAcceptedTerms || !preloadedCustomQuote.hasPreloadedQuote || isSubmittingChangeRequest}
                                  onClick={() => approveProposedTerms()}
                                >
                                  ACCEPT TERMS
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Container>
                      </Grid>
                    )}
                    {
                      ((lastCustomQuote?.action === 'Requested') && source === 'nportal') && (
                        <Grid container xs={12} spacing={2} direction="column" className={classes.pricingCard}>
                          <Container>
                            <Grid container alignItems="center" style={{ marginTop: 16 }}>
                              <Grid item xs={6}>
                                <Box mr={2} display="flex" justifyContent="flex-end">
                                  <InformationButton
                                    hidden={true}
                                    style={{ width: '250px' }}
                                    size="large"
                                    variant="contained"
                                    color="primary"
                                    testId="editLoanTerms"
                                    onClick={() => console.log('Negotiate Terms')}
                                    informationDialogProps={{
                                      title: 'Revise Pricing Terms',
                                      body: (
                                        <>
                                          {renderUnderwriterComment()}
                                          <LoanTermDetailsLoader
                                            updateLoan={updateLoan}
                                            {...preloadedCustomQuote}
                                            loanDetails={loanDetails}
                                          />
                                        </>
                                      ),
                                      maxWidth: 'lg',
                                      dialogOpen: isSubmitDialogOpen,
                                      setDialogOpen: setIsSubmitDialogOpen,

                                    }}
                                  >
                                    REVISE
                                  </InformationButton>
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Box mr={2} display="flex" justifyContent="flex-start">
                                  <Button
                                    style={{ background: '#34A853', width: '250px' }}
                                    hidden={true}
                                    size="large"
                                    variant="contained"
                                    color="primary"
                                    testId="editLoanData"
                                    disabled={!preloadedCustomQuote.hasPreloadedQuote || isSubmittingChangeRequest}
                                    onClick={() => approveProposedTerms()}
                                  >
                                    ACCEPT
                                  </Button>
                                </Box>
                              </Grid>
                            </Grid>
                          </Container>
                        </Grid>
                      )
                    }
                  </Grid>
                </LoanDetailsCustomCard>
              </Grid>
            </LoanDetailsCustomCard>
          </Container>
          <Container className={classes.pricingCard}>
            <LoanDetailsPaper style={{ paddingBottom: 16, paddingTop: 16 }}>
              <Container>
                <Box fontSize={20} fontWeight={600}>
                  Update Log
                </Box>
              </Container>
              <Container>
                {/* Scrollable Update Log */}
                <Box style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {logs.length ? (
                    logs.map((log, idx) => (
                      <Grid className={classes.updateLogItem} item xs={12} key={`${log.label}-${idx}`}>
                        <LoanDetailsInfoItem
                          valueStyle={{ fontSize: 14, fontWeight: 'normal' }}
                          value={log.value}
                          label={log.label}
                        />
                      </Grid>
                    ))
                  ) : (
                    <Box p={2} color="text.secondary">
                      No loan change requests have been logged for this loan yet.
                    </Box>
                  )}
                </Box>
              </Container>
            </LoanDetailsPaper>
          </Container>
          {(showReviseTermButton) &&
            (<Container>
              <Grid container xs={12} className={classes.pricingCard} spacing={2} justifyContent="center">
                <Grid item xs={3} justifyContent='center'>
                  <InformationButton
                    hidden={true}
                    size="large"
                    fullWidth
                    variant="contained"
                    color="primary"
                    testId="editLoanTerms"
                    onClick={() => console.log('Negotiate Terms')}
                    informationDialogProps={{
                      title: 'Revise Pricing Terms',
                      body: (
                        <>
                          {renderUnderwriterComment()}
                          <LoanTermDetailsLoader
                            updateLoan={updateLoan}
                            {...preloadedCustomQuote}
                            loanDetails={loanDetails}
                          />
                        </>
                      ),
                      maxWidth: 'lg',
                      dialogOpen: isSubmitDialogOpen,
                      setDialogOpen: setIsSubmitDialogOpen,
                    }}
                    disabled={hideReviseTerms}
                  >
                    REVISE TERMS
                  </InformationButton>
                </Grid>
              </Grid>
            </Container>)
          }
        </Container>
        :
        <Grid item xs={12} className={classes.customHeight}>
          <NotFullySubmittedAlert />
        </Grid>
      }
    </>
  );
});
